import { ReactNode, useState, useCallback } from 'react';
import { useHits } from 'react-instantsearch-hooks-web';
import useDynamicFacets from '../../hooks/useDynamicFacets';
import { FacetConfiguration } from '../../types';
import { useProductList } from '../../context';
import { useParams } from 'next/navigation';
import AccordionBtn from '@/components/atoms/project-builder/accordion';

interface Props {
  facetsConfiguration: Record<string, FacetConfiguration>;
  title?: string;
  isProjectBuilderPath?: boolean;
  isMediumDesktop?: boolean;
}
interface NoResultProps {
  fallback: ReactNode;
}

function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}

const DesktopFacets: React.FC<Props> = ({ facetsConfiguration, title, isProjectBuilderPath, isMediumDesktop }) => {
  const { locale } = useParams();
  const { results} = useHits();
  const { removeAllRefinements } = useProductList();

  const [openFacets, setOpenFacets] = useState<Set<string>>(new Set());
  const [forceToggle, setForceToggle] = useState<number>(0);
  const [forceState, setForceState] = useState<boolean>(false);

  const handleFacetChange = useCallback((attribute: string, isOpen: boolean) => {
    setOpenFacets((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(attribute);
      else next.delete(attribute);
      return next;
    });
  }, []);

  const filteredFacetsConfiguration = Object.fromEntries(
    Object.entries(facetsConfiguration).filter(([key]) => {
      const isNz = locale?.toLowerCase().includes('nz');
      const isAu = locale?.toLowerCase().includes('au');
      if (isNz) {
        return (
          !key.includes('IsAS14281Compliant') &&
          !key.includes('IsAS14281Ambulant') &&
          !key.includes('en-AU')
        );
      }
      else if (isAu) {
        return (
          !key.includes('IsNZ41121Compliant') &&
          !key.includes('en-NZ')
        );
      }
      return true;
    })
  );

  let visibleCount = 0;
  let openVisibleCount = 0;
  Object.keys(filteredFacetsConfiguration).forEach((attribute) => {
    const facet = filteredFacetsConfiguration[attribute];
    const isEmptyFacet =
      (facet?.type === 'color' || facet?.type === 'term' || facet?.type === 'boolean' || facet?.type === 'range') &&
      !results?.disjunctiveFacets?.find((f) => f.name === attribute);

    if (!isEmptyFacet) {
      visibleCount++;
      if (openFacets.has(attribute)) {
        openVisibleCount++;
      }
    }
  });

  const allExpanded = visibleCount > 0 && openVisibleCount === visibleCount;

  const handleToggleAll = () => {
    const newState = !allExpanded;
    setForceState(newState);
    setForceToggle((prev) => prev + 1);
    
    if (newState) {
      const nextOpen = new Set(openFacets);
      Object.keys(filteredFacetsConfiguration).forEach((attr) => nextOpen.add(attr));
      setOpenFacets(nextOpen);
    } else {
      setOpenFacets(new Set());
    }
  };

  const facets = useDynamicFacets({
    configuration: filteredFacetsConfiguration,
    ordering: results?.renderingContent?.facetOrdering?.facets?.order,
    render: ({ attribute, Component }) => (
      <AccordionBtn
        key={attribute}
        closedSectionTitle={filteredFacetsConfiguration[attribute].label}
        className="min-w-[15rem] border-b border-[#808080] 2xl:min-w-[280px]"
        buttonClassName="py-[16px] text-[14px] 2xl:text-[16px] font-normal text-[#090909] text-left pl-[15px]"
        panelClassName="text-[14px] 2xl:text-[16px] text-[#090909] font-normal w-[15rem] 2xl:min-w-[280px]"
        iconClassName="w-[18px] text-primary-black"
        defaultOpen={false}
        forceToggle={forceToggle}
        forceState={forceState}
        onStateChange={(isOpen) => handleFacetChange(attribute, isOpen)}
      >
        <span>{Component}</span>
      </AccordionBtn>
    ),
  });

  return (
    <div className=" items-center justify-between pb-[16px] pr-[3px]">
      <div className="flex flex-col lg:min-w-[280px] 2xl:min-w-[280px]">
        {!isProjectBuilderPath && isMediumDesktop && title ? (
          <div className="flex items-end justify-between border-b border-neutral-500 pb-[16px] mb-[16px] mt-3">
            <h3 className="text-[32px] font-medium text-[#333] leading-none">{title}</h3>
            <div className="flex items-center gap-[18px]">
              <NoResultsBoundary fallback={<span></span>}>
                <span
                  className="text-[16px] text-[#333] underline hover:cursor-pointer"
                  onClick={handleToggleAll}
                >
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </span>
                <span
                  className="text-[16px] text-[#333] underline hover:cursor-pointer"
                  onClick={removeAllRefinements}
                >
                  Clear All
                </span>
              </NoResultsBoundary>
            </div>
          </div>
        ) : (
          <div className="self-end gap-2 flex pb-[8px]">
            <NoResultsBoundary fallback={<span></span>}>
              <span
                className="text-[12px] text-[#333] underline hover:cursor-pointer 2xl:text-[16px] mr-[15px] xl:mr-0"
                onClick={handleToggleAll}
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </span>
              <span
                className="text-[12px] text-[#333] underline hover:cursor-pointer 2xl:text-[16px] mr-[15px] xl:mr-0"
                onClick={removeAllRefinements}
              >
                Clear All
              </span>
            </NoResultsBoundary>
          </div>
        )}
        {facets}
      </div>
    </div>
  );
};

export default DesktopFacets;
